//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import moment from 'moment-timezone';

import {
  AudienceTargetingSections,
  BOX_TYPE_BRANDED,
  BOX_TYPE_CLIENT_PROVIDED,
  BOX_TYPE_PLAIN,
  DisplayReviews,
  FulfillmentDetails,
  FulfillmentType,
  MemberTasks,
  PACKAGE_INSERT_CLIENT_PROVIDED,
  PACKAGE_INSERT_NONE,
  QUOTE_LOADING_STATUSES,
  SamplingCampaignRequestLocale,
  SamplingCampaignRequestQuoteStatus,
  WhoShipsProduct,
} from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import {
  PurchaseOption,
  SamplingContentType,
  SamplingDeliveryMethod,
  SamplingOpportunityCartonType,
  SamplingOpportunityInsertType,
  SamplingProductType,
} from '@apis/campaign-requests/sampling/opportunities/samplingOpportunitiesApiModel';
import { BVSamplingProduct } from '@apis/campaign-requests/sampling/products/samplingProductsApiModel';
import {
  CampaignRequestType,
  CommunityType,
} from '@apis/campaign-requests/shared/CampaignRequests';
import { CAMPAIGN_REQUEST_LOCALES } from '@campaigns/constants/CampaignRequestsLocaleConstants';
import { DATE_FORMAT_MMM_DD_YYYY } from '@campaigns/constants/DateConstants';
import {
  BV_SAMPLING_PROGRAM,
  DEFAULT_HASHTAGS,
  EXPANDED_SECTIONS_MAPPING,
  INFLUENSTER_HANDLE,
} from '@campaigns/constants/sampling/SamplingCampaignRequestConstants';
import { DEFAULT_EMPTY_VALUE } from '@constants/DefaultEmptyValues';
import { isDefined, isUndefined } from '@utils/filterPredicates';

export const isBVSamplingProgram = (program: string) =>
  program === BV_SAMPLING_PROGRAM;

/**
 * A one-off creator campaign is a Sampling request tagged with the creator
 * community, created by clients who are not subscribed to creator campaigns.
 * Subscribed clients get `type: 'creators'` instead, which is a different flow.
 */
export const isOneOffCreatorRequest = (
  type?: CampaignRequestType,
  communityType?: CommunityType,
) =>
  type === CampaignRequestType.Sampling &&
  communityType === CommunityType.Creator;

export const getPurchaseOptionProductTypes = (
  type?: CampaignRequestType,
  communityType?: CommunityType,
) =>
  isOneOffCreatorRequest(type, communityType)
    ? [SamplingProductType.CreatorUnit]
    : undefined;

export const getEffectiveSamplingTasks = (
  memberTasks: MemberTasks | undefined,
  communityType?: CommunityType,
): MemberTasks | undefined => {
  if (communityType !== CommunityType.Creator) {
    return memberTasks;
  }
  const instagramPost = memberTasks?.socials?.find(
    ({ platform }) => platform === 'instagramPost',
  );
  return {
    ...memberTasks,
    postOnSocial: true,
    postSurvey: false,
    socials: [
      instagramPost ?? {
        platform: 'instagramPost',
        handle: INFLUENSTER_HANDLE,
        hashtag: DEFAULT_HASHTAGS,
      },
    ],
    writeReview: false,
  };
};

export const hasProductsWithMissingPlatformAllocations = (
  products: BVSamplingProduct[] | undefined,
): boolean =>
  products?.some((product) => {
    return product.variations?.some((variation) => {
      const instagramQuantity = variation.allocations?.find(({ tasks }) =>
        tasks.includes('instagram'),
      )?.quantity;
      const tiktokQuantity = variation.allocations?.find(({ tasks }) =>
        tasks.includes('tiktok'),
      )?.quantity;

      return (
        isUndefined(instagramQuantity) ||
        isUndefined(tiktokQuantity) ||
        (instagramQuantity === 0 && tiktokQuantity === 0)
      );
    });
  }) ?? false;

export const hasProductsWithInvalidPlatformAllocations = (
  products: BVSamplingProduct[] | undefined,
): boolean =>
  products?.some((product) =>
    product.variations?.some((variation) =>
      hasInvalidPlatformAllocation(product, variation),
    ),
  ) ?? false;

export const hasInvalidPlatformAllocation = (
  product: BVSamplingProduct,
  variation: NonNullable<BVSamplingProduct['variations']>[number],
): boolean => {
  const expectedQuantity =
    product.variations?.length === 1
      ? product.quantity ?? variation.quantity
      : variation.quantity;
  const instagramQuantity = variation.allocations?.find(({ tasks }) =>
    tasks.includes('instagram'),
  )?.quantity;
  const tiktokQuantity = variation.allocations?.find(({ tasks }) =>
    tasks.includes('tiktok'),
  )?.quantity;

  // If no allocation has been set for either platform (both absent or both 0),
  // treat the variation as unset rather than invalid. The campaign-request
  // validation catches this at submit time.
  const hasNoAllocations =
    instagramQuantity === undefined && tiktokQuantity === undefined;
  const bothPlatformsAllocationsAbsent =
    instagramQuantity === 0 && tiktokQuantity === 0;
  if (hasNoAllocations || bothPlatformsAllocationsAbsent) {
    return false;
  }

  return (
    typeof expectedQuantity === 'number' &&
    (instagramQuantity ?? 0) + (tiktokQuantity ?? 0) !== expectedQuantity
  );
};

export const getLocaleDetails = (value?: SamplingCampaignRequestLocale) => {
  if (!value) {
    return undefined;
  }
  return CAMPAIGN_REQUEST_LOCALES.find((locale) => locale.value === value);
};

export const getWhoShipsProductText = (
  whoShipsProduct: WhoShipsProduct,
): string => {
  if (whoShipsProduct === 'bazaarvoice') {
    return 'Shipped by Bazaarvoice';
  }

  return 'Shipped by my company';
};

/** Whether Bazaarvoice ships the products to participants */
export const isShippedByBazaarvoice = (
  fulfillmentOptions: FulfillmentDetails | undefined,
): boolean =>
  fulfillmentOptions?.fulfillmentType === 'shipped' &&
  fulfillmentOptions.whoShipsProduct === 'bazaarvoice';

export const getSamplingProgramText = (
  samplingProgram: string | undefined,
): string => {
  if (samplingProgram) {
    return isBVSamplingProgram(samplingProgram)
      ? 'Bazaarvoice Sampling Community'
      : 'Client Community';
  } else {
    return DEFAULT_EMPTY_VALUE;
  }
};

export const getManagedByText = (
  fulfillmentType: FulfillmentType | undefined,
) => {
  if (fulfillmentType === 'shipped') {
    return 'Shipped products provided';
  } else if (fulfillmentType === 'deals') {
    return 'Coupon codes provided';
  }

  return undefined;
};

export const getFulfillmentOptionsFromPurchaseOptions = (
  purchaseOptions: PurchaseOption[],
): FulfillmentDetails => {
  if (purchaseOptions.length === 0) {
    return {};
  }

  const deliveryMethods = [
    ...new Set(purchaseOptions.map((po) => po.deliveryMethod)),
  ];
  const deliveryMethod = deliveryMethods[0];

  if (deliveryMethods.length > 1 || !deliveryMethod) {
    return {};
  }

  let fulfillmentDetails: FulfillmentDetails = {};

  switch (deliveryMethod) {
    case SamplingDeliveryMethod.Shipped:
      fulfillmentDetails = {
        fulfillmentType: 'shipped',
        whoShipsProduct: 'myCompany',
      };
      break;
    case SamplingDeliveryMethod.ShippedByBazaarvoice:
      fulfillmentDetails = {
        fulfillmentType: 'shipped',
        whoShipsProduct: 'bazaarvoice',
      };
      break;
    case SamplingDeliveryMethod.DigitalDelivery:
      fulfillmentDetails = {
        fulfillmentType: 'deals',
      };
      break;
    default:
      return {};
  }

  const allFulfillmentOptions = purchaseOptions.flatMap(
    (po) => po.fulfillmentOptions ?? [],
  );

  if (allFulfillmentOptions.length > 0) {
    const firstInsert = allFulfillmentOptions[0]?.insert;
    const allSameInsert = allFulfillmentOptions.every(
      (fo) => fo.insert === firstInsert,
    );

    if (allSameInsert && firstInsert) {
      if (firstInsert === SamplingOpportunityInsertType.NO) {
        fulfillmentDetails.packageInsert = PACKAGE_INSERT_NONE;
      } else if (
        firstInsert === SamplingOpportunityInsertType.CLIENT_SUPPLIED
      ) {
        fulfillmentDetails.packageInsert = PACKAGE_INSERT_CLIENT_PROVIDED;
      }
    }

    const firstCartonType = allFulfillmentOptions[0]?.cartonType;
    const allSameCartonType = allFulfillmentOptions.every(
      (fo) => fo.cartonType === firstCartonType,
    );

    if (allSameCartonType && firstCartonType) {
      if (firstCartonType === SamplingOpportunityCartonType.PLAIN) {
        fulfillmentDetails.boxType = BOX_TYPE_PLAIN;
      } else if (
        firstCartonType === SamplingOpportunityCartonType.CLIENT_SUPPLIED
      ) {
        fulfillmentDetails.boxType = BOX_TYPE_CLIENT_PROVIDED;
      } else if (
        firstCartonType === SamplingOpportunityCartonType.TWO_SIDED_CUSTOM
      ) {
        fulfillmentDetails.boxType = BOX_TYPE_BRANDED;
      }
    }
  }

  return fulfillmentDetails;
};

export const getMemberTasksFromPurchaseOptions = (
  purchaseOptions: PurchaseOption[],
): { displayReviews: DisplayReviews; memberTasks: MemberTasks } => {
  const memberTasks: MemberTasks = {
    postOnSocial: false,
    postSurvey: false,
    writeReview: false,
  };
  const displayReviews: DisplayReviews = {};

  if (purchaseOptions.length === 0) {
    return { displayReviews, memberTasks };
  }

  const contentTypes = [
    ...new Set(purchaseOptions.map((po) => po.contentType)),
  ];
  const contentType = contentTypes.length === 1 ? contentTypes[0] : undefined;

  const postSurveyValues = [
    ...new Set(purchaseOptions.map((po) => po.hasPostSurvey)),
  ];
  const hasPostSurvey =
    postSurveyValues.length === 1 ? postSurveyValues[0] : undefined;

  if (contentType === SamplingContentType.REVIEWS_ONLY) {
    memberTasks.writeReview = true;
  } else if (contentType === SamplingContentType.SOCIAL_ONLY) {
    memberTasks.postOnSocial = true;
  } else if (contentType === SamplingContentType.REVIEWS_AND_SOCIAL) {
    memberTasks.writeReview = true;
    memberTasks.postOnSocial = true;
  }

  if (hasPostSurvey) {
    memberTasks.postSurvey = true;
  }

  return { displayReviews, memberTasks };
};

export const isKeyOfAudienceTargetingSections = (
  key: string,
): key is keyof AudienceTargetingSections => {
  return key in EXPANDED_SECTIONS_MAPPING;
};

export const getNotificationsTitle = (
  whoShipsProduct: WhoShipsProduct | undefined,
) => {
  return whoShipsProduct === 'bazaarvoice'
    ? 'Warehouse notifications'
    : 'Fulfillment file notifications';
};

export const filterValidAndSelectedOpportunities = (
  opportunities: PurchaseOption[] | undefined,
  selectedOptions: PurchaseOption[],
): PurchaseOption[] => {
  const currentDate = moment();

  return (
    opportunities?.filter((opportunity) => {
      const expiryDate = moment(opportunity.expiryDate);
      const isExpired =
        expiryDate.isValid() && expiryDate.isBefore(currentDate);
      const isSelected = selectedOptions.some(
        (selectedOption) =>
          selectedOption.opportunityRef === opportunity.opportunityRef,
      );
      const hasPositiveBalance =
        isDefined(opportunity.balance) && opportunity.balance > 0;

      return (hasPositiveBalance && !isExpired) || isSelected;
    }) ?? []
  );
};

export const haveProductsInCampaignLaunched = (
  targetPublishDate: Date | undefined,
) => {
  return (
    !targetPublishDate ||
    (targetPublishDate && moment(targetPublishDate).isBefore(moment()))
  );
};

const CAMPAIGN_ID_DISPLAY_LENGTH = 6;
const DEFAULT_CAMPAIGN_NAME = 'New Campaign';

export const formatCampaignDisplayName = (
  ugcCampaignRequestId: string | undefined,
  clientCampaignName: string | undefined,
): string => {
  if (!isDefined(ugcCampaignRequestId)) {
    return clientCampaignName ?? DEFAULT_CAMPAIGN_NAME;
  }

  const truncatedId = ugcCampaignRequestId
    .substring(0, CAMPAIGN_ID_DISPLAY_LENGTH)
    .toUpperCase();
  const campaignName = clientCampaignName ?? DEFAULT_CAMPAIGN_NAME;

  return `${truncatedId} | ${campaignName}`;
};

export const formatCampaignDates = (createdAt?: Date, expiredAt?: Date) => {
  if (isDefined(createdAt) && isDefined(expiredAt)) {
    return `${moment(createdAt).format(DATE_FORMAT_MMM_DD_YYYY)} - ${moment(expiredAt).format(DATE_FORMAT_MMM_DD_YYYY)}`;
  }

  if (isDefined(createdAt)) {
    return moment(createdAt).format(DATE_FORMAT_MMM_DD_YYYY);
  }

  return undefined;
};

export enum QuoteOutcome {
  VALID_QUOTE = 'VALID_QUOTE',
  EXPIRED_QUOTE = 'EXPIRED_QUOTE',
  NOT_QUOTABLE = 'NOT_QUOTABLE',
  ERROR = 'ERROR',
  LOADING = 'LOADING',
}

export const determineQuoteOutcome = ({
  isQuoteError,
  isQuoteValid,
  status,
}: {
  isQuoteError: boolean;
  isQuoteValid: boolean;
  status?: SamplingCampaignRequestQuoteStatus;
}): QuoteOutcome | undefined => {
  if (
    isQuoteError ||
    (isQuoteValid &&
      (status === SamplingCampaignRequestQuoteStatus.ERROR_INITIALISING ||
        status === SamplingCampaignRequestQuoteStatus.ERROR_QUOTING))
  ) {
    return QuoteOutcome.ERROR;
  }

  if (
    isDefined(status) &&
    !isQuoteValid &&
    QUOTE_LOADING_STATUSES.includes(status)
  ) {
    return QuoteOutcome.LOADING;
  }

  if (isQuoteValid) {
    switch (status) {
      case SamplingCampaignRequestQuoteStatus.QUOTED:
        return QuoteOutcome.VALID_QUOTE;
      case SamplingCampaignRequestQuoteStatus.EXPIRED:
        return QuoteOutcome.EXPIRED_QUOTE;
      case SamplingCampaignRequestQuoteStatus.NOT_QUOTABLE:
        return QuoteOutcome.NOT_QUOTABLE;
    }
  }

  return undefined;
};
